import { type ElementType, useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  KeyRound,
  Loader2,
  Pencil,
  Save,
  ShieldCheck,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { getLicense, validateLicense } from "../../api/frameworksApi";


const LICENSE_TIERS = [
  { tier: 1, label: "Tier 1", ssps: 10 },
  { tier: 2, label: "Tier 2", ssps: 50 },
  { tier: 3, label: "Tier 3", ssps: 100 },
] as const;

function getTierForSSPs(entitled: number): (typeof LICENSE_TIERS)[number] | null {
  return LICENSE_TIERS.find((t) => t.ssps === entitled) ?? null;
}

interface LicenseInfo {
  entity: string;
  entitled: number;
  used: number;
  startDate: string;
  validTill: string;
  validTillRaw: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function LicenseManagement() {
  const [licenseData, setLicenseData] = useState("");
  const [savedLicenseData, setSavedLicenseData] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    getLicense()
      .then((res) => {
        const d = res.data;
        const key = d.licenseKey ?? "";
        setLicenseData(key);
        setSavedLicenseData(key);
        setLicenseInfo({
          entity: d.userName,
          entitled: d.sspLimit,
          used: d.currentSspCount,
          startDate: formatDate(d.startDate),
          validTill: formatDate(d.expiryDate),
          validTillRaw: d.expiryDate ?? "",
        });
      })
      .catch(() => {
        useToastStore.getState().showError("Error", "Failed to load license information. Please refresh the page.");
        setLicenseInfo({ entity: "", entitled: 0, used: 0, startDate: "", validTill: "", validTillRaw: "" });
      });
  }, []);

  const handleToggleKey = useCallback(() => {
    const next = !showKey;
    setShowKey(next);

    if (isEditing) {
      setIsEditing(false);
      setLicenseData(savedLicenseData);
    }

    if (next) {
      const trimmed = savedLicenseData.trim();
      if (!trimmed) return;

      setIsValidating(true);
      validateLicense(trimmed)
        .then((result) => {
          if (result.valid) {
            const { license } = result;
            setLicenseInfo({
              entity: license.userName,
              entitled: license.sspLimit,
              used: result.currentSspCount,
              startDate: formatDate(license.startDate),
              validTill: formatDate(license.expiryDate),
              validTillRaw: license.expiryDate ?? "",
            });
          } else if (result.expired) {
            useToastStore.getState().showError("Error", "License key has expired. Please provide a valid license key");
          } else if (result.notYetActive) {
            useToastStore.getState().showError("Error", "License key is not yet active. Please check the activation date");
          } else {
            useToastStore.getState().showError("Error", result.message || "Invalid license key");
          }
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : "Failed to validate license key";
          useToastStore.getState().showError("Error", message);
        })
        .finally(() => {
          setIsValidating(false);
        });
    }
  }, [showKey, isEditing, savedLicenseData]);

  const remainingSSPs = licenseInfo ? licenseInfo.entitled - licenseInfo.used : 0;

  const currentTier = licenseInfo ? getTierForSSPs(licenseInfo.entitled) : null;

  const remainingDays = useMemo(() => {
    if (!licenseInfo?.validTillRaw) return 0;
    const end = new Date(licenseInfo.validTillRaw);
    if (isNaN(end.getTime())) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.max(0, Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  }, [licenseInfo?.validTillRaw]);

  const handleSave = useCallback(async () => {
    const trimmed = licenseData.trim();

    if (!trimmed) {
      useToastStore.getState().showError("Error", "License key cannot be empty");
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateLicense(trimmed);

      if (!result.valid) {
        if (result.expired) {
          useToastStore.getState().showError("Error", "License key has expired. Please provide a valid license key");
        } else if (result.notYetActive) {
          useToastStore.getState().showError("Error", "License key is not yet active. Please check the activation date");
        } else {
          useToastStore.getState().showError("Error", result.message || "Invalid license key");
        }
        return;
      }

      const { license } = result;
      setLicenseInfo({
        entity: license.userName,
        entitled: license.sspLimit,
        used: result.currentSspCount,
        startDate: formatDate(license.startDate),
        validTill: formatDate(license.expiryDate),
        validTillRaw: license.expiryDate ?? "",
      });

      setSavedLicenseData(licenseData);
      setIsEditing(false);
      useToastStore.getState().showSuccess("Success", result.message || "License key saved successfully");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to validate license key";
      useToastStore.getState().showError("Error", message);
    } finally {
      setIsValidating(false);
    }
  }, [licenseData]);

  const handleCancel = () => {
    setLicenseData(savedLicenseData);
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-[#0f172a] dark:text-white">License Management</h1>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
              <ShieldCheck className="size-3" />
              Active
            </span>
          </div>
          <p className="mt-1 text-xs text-[#64748b] dark:text-white/40">
            Review entitlement usage and maintain the current license for your environment.
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {licenseInfo === null ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] px-4 py-3 shadow-sm">
              <div className="h-3 w-24 rounded bg-white/10 dark:bg-white/10 animate-pulse mb-3" />
              <div className="h-5 w-16 rounded bg-white/10 dark:bg-white/10 animate-pulse" />
            </div>
          ))
        ) : (
          <>
            <SummaryCell icon={Building2} label="Registered Entity" value={licenseInfo.entity} />
            <SummaryCell icon={KeyRound} label="Entitled SSP" value={String(licenseInfo.entitled)} sub={currentTier ? currentTier.label : undefined} />
            <SummaryCell icon={BadgeCheck} label="Used SSP" value={`${licenseInfo.used} / ${licenseInfo.entitled}`} sub={`${remainingSSPs} remaining SSPs`} />
            <SummaryCell icon={CalendarDays} label="License Effective Date" value={licenseInfo.startDate || "—"} />
            <SummaryCell icon={ShieldCheck} label="Valid Till" value={licenseInfo.validTill || "—"} sub={licenseInfo.validTill ? `${remainingDays} days remaining` : undefined} />
          </>
        )}
      </div>

      {/* License Key */}
      <div className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden">
        {/* Side tab layout */}
        <div className="flex min-h-[300px]">
          {/* Left tab — 20% */}
          <div className="w-1/5 border-r border-[#f1f5f9] dark:border-white/[0.04] flex flex-col p-3">
            <button
              type="button"
              onClick={handleToggleKey}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-bold transition-all ${
                showKey
                  ? "bg-indigo-600 text-white shadow-sm border-2 border-indigo-600"
                  : "bg-white dark:bg-white/[0.04] text-[#475569] dark:text-white/50 border-2 border-indigo-400 dark:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
              }`}
            >
              <KeyRound className="size-3.5 shrink-0" />
              License Key
            </button>
          </div>

          {/* Right content — 80% */}
          <div className="w-4/5 flex flex-col">
            {!showKey ? (
              <div className="flex-1 flex items-center justify-center text-xs text-[#94a3b8] dark:text-white/30">
                  Click "License Key Button" to view the license key
              </div>
            ) : (
              <>
                {!isEditing ? (
                  <div className="flex-1 p-5 overflow-y-auto">
                    {licenseData.trim() ? (
                      <pre
                        className="font-mono text-xs leading-relaxed text-[#0f172a] dark:text-white whitespace-pre-wrap break-all select-none pointer-events-none"
                        style={{ filter: 'blur(1.5px)' }}
                      >
                        {licenseData}
                      </pre>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full gap-3 py-10">
                        <div className="flex items-center justify-center size-10 rounded-full bg-indigo-500/10 dark:bg-indigo-500/15">
                          <KeyRound className="size-5 text-indigo-400" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold text-[#475569] dark:text-white/50">No license key found</p>
                          <p className="mt-0.5 text-[11px] text-[#94a3b8] dark:text-white/30">Click "Edit Key" to add your license key</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 p-5 flex flex-col">
                    <textarea
                      id="license-data"
                      value={licenseData}
                      onChange={(event) => setLicenseData(event.target.value)}
                      rows={8}
                      spellCheck={false}
                      placeholder="Paste your new license key here"
                      className="flex-1 w-full resize-none overflow-y-auto rounded-lg border px-3 py-2 font-mono text-xs leading-relaxed outline-none transition-all text-[#0f172a] dark:text-white placeholder:text-[#94a3b8] dark:placeholder:text-white/25 border-indigo-500 dark:border-indigo-500 bg-white dark:bg-white/[0.04] ring-2 focus:ring-indigo-500/10 focus:border-indigo-500"
                    />
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[#f1f5f9] dark:border-white/[0.04]">
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2 h-8 px-3 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-xs font-bold text-[#475569] dark:text-white/70 transition-all hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                    >
                      <Pencil className="size-3" />
                      Edit Key
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isValidating}
                        className="flex items-center gap-2 h-8 px-3 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-xs font-bold text-[#475569] dark:text-white/70 transition-all hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isValidating}
                        className="flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-bold text-white bg-indigo-600 transition-all hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isValidating ? (
                          <>
                            <Loader2 className="size-3 animate-spin" />
                            Validating…
                          </>
                        ) : (
                          <>
                            <Save className="size-3" />
                            Save
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCell({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: ElementType;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30">
        <Icon className="size-3.5 text-indigo-500 dark:text-indigo-400" />
        {label}
      </div>
      <p className="mt-1.5 text-sm font-black text-[#0f172a] dark:text-white">{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-[#94a3b8] dark:text-white/30">{sub}</p>}
    </div>
  );
}

